/**
 * skin_material — the material-level skin response of the near versona actors (shader gaps program round 2, agent D,
 * 2026-09-29). The pure rules are `skin_shading.ts`; this file is their TSL twin and the plumbing that puts it on the
 * rig actors.
 *
 * THE HOOK. A rig master's material is a classic `MeshPhysicalMaterial` (the Meshy GLBs carry KHR_materials_ior +
 * _specular). `SkinNodeMaterial` is a `MeshPhysicalNodeMaterial` built from it (every property copied, as three's own
 * `NodeLibrary.fromMaterial` does), whose lighting model extends three's `PhysicalLightingModel`:
 *   · `start()` — once per fragment, at the top of the shader (THE LAZY-VAR LAW: the curvature's derivatives are
 *     taken here, never inside a light): the skin weight from the albedo's chroma (`skinMaskNode`) × the bone-region
 *     gate (a vertex varying over skinIndex / skinWeight and the body's bone mask) × the global mix; the curvature and
 *     the chord from the BAKED per-vertex attribute `skinGeo` (`skin_curvature.ts` — Penner's per-pixel |dN|/|dP| is
 *     noise on an 8-bit-normal, seam-split Meshy rig: red sparkles on a back-lit face), converted from the geometry's
 *     units to mm by the pixel's own ratio |d P_view| / |d P_geometry| (both continuous across seams, exact per
 *     triangle — no CPU scale); the curvature's LUT row; the transmittance through the scaled chord (Jimenez 2010's
 *     profile); the "blurred" normal (the geometric normal, Penner's option A — or the shading normal when the DQ
 *     patch owns `normalNode`).
 *   · `direct()` — per light, AFTER three's own diffuse + specular: adds the DIFFERENCE between the skin response and
 *     three's Lambert, so a texel whose mask is 0 changes by exactly nothing and the specular is never touched. The
 *     response (FaceWorks' EvaluateSSSDirectLight, bent normals, then the deep-scatter back lobe) is written out in
 *     `skin_shading.ts` and `skinDiffuseCpu` is its oracle. Every direct light — the sun, the moon, a lamp, a fire —
 *     gets it, shadowed or not, because it rides the light's own `lightColor`.
 *   · `finish()` — the debug views (`__skin().debug(1|2|3|4)`: the mask, the curvature row, the transmittance, the last
 *     direct light's N_b·L — the bed's terminator band).
 *
 * PER-BODY VALUES LIVE ON THE MATERIAL, READ BY `materialReference` (the luminance floor, the bone mask): two bodies'
 * skin materials may share one compiled program (with the DQ patch off their node properties are identical), and a
 * value closed over by the lighting model would be the FIRST body's for both (the cave-light law). The global knobs
 * are shared uniforms. The LUT is one HalfFloat texture for every body (the engine's `bakeSkinCurvatureLut`).
 *
 * THE SWAP. `registerRigSkin(root, key)` (called by `makeRigActor` after the DQ patch — so the DQ nodes are copied
 * with everything else) records every skinned mesh with its original material in a WeakRef registry; while
 * `skinShading` is on (and `localStorage 'atlas:skin' !== 'off'`) the mesh wears the body's skin material, and
 * `syncSkinShading()` (a per-frame boolean check from the rig layer) swaps every live actor back and forth when the
 * flag moves. Off = the original material object, exactly what drew before. A mesh swaps only once its geometry
 * carries `skinGeo` — baked ONCE per body (every actor clone shares the geometry) in `workers/skin_geo_worker.ts`
 * (~0.6 s in V8 for a 200k-vertex rig, off the main thread); until it lands the mesh keeps its plain material. The
 * bake is asked for only while skin shading is wanted, so with the flag off a geometry is never touched.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, float, vec2, vec3, vec4, uniform, texture, attribute, varying, materialReference, diffuseColor, metalness, property,
  normalView, positionView, positionGeometry, dFdx, dFdy, smoothstep, saturate, clamp,
  max, mix, sqrt,
  exp, atan, dot, length, sRGBTransferOETF, uint, select, step, uv,
} from 'three/tsl';
import { bakeSkinCurvatureLut, type SkinCurvatureLut } from 'enginex/avatar/skin/PreintegratedSkin.js';
import {
  SKIN_CLASSIFIER, SKIN_LUT, SKIN_PROFILE, SKIN_BACK_WRAP, SKIN_BACK_NORM, SKIN_TRANSLUCENCY_SCALE, SKIN_THICKNESS_MIN_MM,
  SKIN_THICKNESS_MAX_MM, SKIN_ALLOW_EDGE, SKIN_DEFAULTS, DEFAULT_SKIN_REGIONS, skinBodyParams, skinBoneMask, skinKilled,
  regionOfBone, type SkinBodyParams,
} from './skin_shading.js';
import { activePreset } from './quality_tiers.js';
import { bakeSkinGeometry } from './skin_curvature.js';
import type { SkinGeoReply, SkinGeoRequest } from '../workers/skin_geo_worker.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TSL graphs are loosely typed
type N = any;

// ── Shared uniforms ────────────────────────────────────────────────────────────────────────────────────────────────

const P = SKIN_CLASSIFIER;
/** 0 = no skin response anywhere (the plain material's arithmetic), 1 = the full response. */
export const uSkinMix: N = uniform(1);
export const uSkinCurvScale: N = uniform(SKIN_DEFAULTS.curvatureScale);
export const uSkinTransmission: N = uniform(SKIN_DEFAULTS.transmission);
/** 0 off · 1 the mask · 2 the curvature row · 3 the transmittance · 4 N_b·L of the last direct light (the bed's views). */
export const uSkinDebug: N = uniform(0);

const DEG = 180 / Math.PI;
const C_MIN = 1 / SKIN_LUT.rMaxMm, C_MAX = 1 / SKIN_LUT.rMinMm;

/** Stats the lever and the bed read. */
export const skinStats = {
  materialsBuilt: 0, lightingModels: 0, meshesRegistered: 0, swapsOn: 0, swapsOff: 0, syncs: 0,
  lutBakeMs: 0, lutEnergyMax: 0, lutBytes: 0,
};

// ── The LUT ────────────────────────────────────────────────────────────────────────────────────────────────────────

let lut: { texture: THREE.DataTexture; data: SkinCurvatureLut } | null = null;
/** The curvature LUT (baked once, ~60 ms, on the first skin material). */
export function skinLut(): { texture: THREE.DataTexture; data: SkinCurvatureLut } {
  if (lut) return lut;
  const t0 = performance.now();
  const data = bakeSkinCurvatureLut({ width: SKIN_LUT.width, height: SKIN_LUT.height, rMinMm: SKIN_LUT.rMinMm, rMaxMm: SKIN_LUT.rMaxMm, steps: SKIN_LUT.steps });
  const half = new Uint16Array(data.data.length);
  for (let i = 0; i < half.length; i++) half[i] = THREE.DataUtils.toHalfFloat(data.data[i]);
  const tex = new THREE.DataTexture(half, data.width, data.height, THREE.RGBAFormat, THREE.HalfFloatType);
  tex.colorSpace = THREE.NoColorSpace;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false;
  tex.flipY = false;
  tex.name = 'AtlasSkinCurvatureLUT';
  tex.needsUpdate = true;
  skinStats.lutBakeMs = +(performance.now() - t0).toFixed(1);
  skinStats.lutBytes = half.byteLength;
  skinStats.lutEnergyMax = +Math.max(...data.energy.flat()).toFixed(4);
  lut = { texture: tex, data };
  return lut;
}

// ── The classifier (the TSL twin of skinWeightSrgb) ────────────────────────────────────────────────────────────────

/** The skin weight of a LINEAR albedo with its metalness, between a luminance floor's soft edge [floor0, floor1]. */
export function skinMaskNode(linRgb: N, metal: N, floor0: N, floor1: N): N {
  const s: N = sRGBTransferOETF(saturate(linRgb));
  const y: N = dot(s, vec3(0.299, 0.587, 0.114));
  const cb: N = dot(s, vec3(-0.168736, -0.331264, 0.5));
  const cr: N = dot(s, vec3(0.5, -0.418688, -0.081312));
  const c: N = length(vec2(cb, cr));
  const rel: N = c.div(max(y, 0.02));
  const phi: N = atan(cr, cb.negate()).mul(DEG);
  const lo: N = float(P.phiLoDark).add(float(P.phiLoLight - P.phiLoDark).mul(smoothstep(P.yLight0, P.yLight1, y)));
  const wPhi: N = smoothstep(lo.sub(P.phiEdge), lo.add(P.phiEdge), phi).mul(float(1).sub(smoothstep(P.phiHi - P.phiHiEdge, P.phiHi + P.phiHiEdge, phi)));
  const wRel: N = smoothstep(P.relLo0, P.relLo1, rel).mul(float(1).sub(smoothstep(P.relHi0, P.relHi1, rel)));
  const wY: N = smoothstep(floor0, floor1, y).mul(float(1).sub(smoothstep(P.yHi0, P.yHi1, y)));
  const wM: N = float(1).sub(smoothstep(P.metal0, P.metal1, metal));
  return wPhi.mul(wRel).mul(wY).mul(wM);
}

/** The vertex's allowed weight: Σ skinWeight over the joints the material's bone mask allows (a varying). */
function allowNode(): N {
  const build = Fn(() => {
    const si: N = attribute('skinIndex', 'uvec4');
    const sw: N = attribute('skinWeight', 'vec4');
    const m: N = uint(materialReference('skinBoneMask', 'float'));
    const bit = (j: N): N => float(m.shiftRight(j).bitAnd(uint(1)));
    return sw.x.mul(bit(si.x)).add(sw.y.mul(bit(si.y))).add(sw.z.mul(bit(si.z))).add(sw.w.mul(bit(si.w)));
  });
  return varying(build(), 'v_skinAllow');
}

/** Jimenez's transmittance T(d) for a slab d mm thick (d² given). */
function transmittanceNode(dd: N): N {
  let acc: N = vec3(0, 0, 0);
  for (const g of SKIN_PROFILE) acc = acc.add(vec3(g.w[0], g.w[1], g.w[2]).mul(exp(dd.div(-g.v))));
  return acc;
}

// ── The lighting model ─────────────────────────────────────────────────────────────────────────────────────────────

const diffuseContribution: N = property('vec3', 'DiffuseContribution');

class SkinLightingModel extends THREE.PhysicalLightingModel {
  private mask: N = null;
  private lutV: N = null;
  private trans: N = null;
  private nb: N = null;
  private curv: N = null;
  private nlDbg: N = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(...flags: any[]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    super(...(flags as [any, any, any, any, any, any]));
    skinStats.lightingModels++;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- three's lighting-model inputs are loosely typed nodes
  start(builder: any): void {
    // THE MASK: chroma × the body's floor × the bone-region gate × the global mix
    const w: N = skinMaskNode(diffuseColor.rgb, metalness, materialReference('skinFloor0', 'float'), materialReference('skinFloor1', 'float'));
    const gate: N = smoothstep(SKIN_ALLOW_EDGE[0], SKIN_ALLOW_EDGE[1], allowNode());
    this.mask = w.mul(gate).mul(uSkinMix).toVar('skinMask');
    // THE BAKED GEOMETRY TERMS (skin_curvature.ts): x = curvature (units⁻¹), y = the inward chord (units). The pixel's
    // metres per geometry unit = |d P_view| / |d P_geometry| (rotation-invariant: dx and dy lengths summed; both are
    // exact per triangle) — taken here, at the top of the shader, never in a light (THE LAZY-VAR LAW)
    const geo: N = attribute('skinGeo', 'vec2');
    const dLen = (v: N): N => length(dFdx(v)).add(length(dFdy(v)));
    const mPerUnit: N = dLen(positionView).div(max(dLen(positionGeometry), 1e-12)).toVar('skinMPerUnit');
    this.curv = geo.x.div(max(mPerUnit, 1e-9)).mul(0.001).mul(uSkinCurvScale).toVar('skinCurv');
    this.lutV = sqrt(clamp(this.curv.sub(C_MIN).div(C_MAX - C_MIN), 0, 1)).toVar('skinLutV');
    // THE TRANSMITTANCE through the scaled chord (Jimenez's translucency factor; red survives farthest)
    const thick: N = clamp(geo.y.mul(mPerUnit).mul(1000 * SKIN_TRANSLUCENCY_SCALE), SKIN_THICKNESS_MIN_MM, SKIN_THICKNESS_MAX_MM);
    this.trans = transmittanceNode(thick.mul(thick)).toVar('skinTrans');
    // THE BLURRED NORMAL = the shading normal (the path this model was calibrated on). Coordinator, 2026-09-30: the DQ
    // patch no longer owns `normalNode` (it dropped the rigs' normal maps), so the old test "a custom normalNode ⇒ the
    // shading normal" would now pick the geometric normal and the diffuse delta LUT(N_b·L) − sat(N·L) goes NEGATIVE at
    // the terminator wherever the map tilts N toward the light (the bed's terminator / back-lit checks). The geometric
    // normal (Penner's option A) needs its own calibration pass before it is used here.
    this.nb = normalView.toVar('skinNb');
    this.nlDbg = float(-1).toVar('skinNlDbg'); // debug view 4: the last direct light's N_b·L (the bed's terminator band)
    super.start(builder);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  direct(lightData: any, builder: any): void {
    super.direct(lightData, builder);
    if (!this.mask) return;
    const L: N = lightData.lightDirection;
    const n: N = normalView;
    const nb: N = this.nb;
    const nlS: N = saturate(dot(n, L)); // three's own Lambert term (PhysicalLightingModel.direct)
    const nlB: N = dot(nb, L);
    this.nlDbg.assign(nlB);
    const f: N = saturate(float(1).sub(nlB));
    const f2: N = f.mul(f);
    const safeNorm = (v: N): N => v.div(max(length(v), 1e-4));
    const nG: N = safeNorm(mix(n, nb, f2.mul(0.7).add(0.3)));
    const nBl: N = safeNorm(mix(n, nb, f2));
    const nl3: N = vec3(saturate(nlB), saturate(dot(nG, L)), saturate(dot(nBl, L)));
    // the scatter delta at (N_b·L, curvature) — an explicit-level sample: legal anywhere, even in a clustered loop
    const delta: N = texture(skinLut().texture, vec2(nlB.mul(0.5).add(0.5), this.lutV)).level(float(0)).rgb;
    const D: N = saturate(delta.add(nl3));
    const front: N = vec3(nlS).add(D.sub(nlS).mul(this.mask));
    // the transmitted fraction weighs the mask SQUARED: a half-skin texel at a hairline (a thin hair shell over painted
    // scalp) transmitted as a glowing speck under a back light on the bed; true skin (mask ≈ 1) is unchanged
    const a: N = this.trans.mul(this.mask.mul(this.mask)).mul(uSkinTransmission);
    const back: N = saturate(float(SKIN_BACK_WRAP).sub(nlB)).mul(SKIN_BACK_NORM);
    const total: N = front.mul(vec3(1).sub(a)).add(a.mul(back));
    lightData.reflectedLight.directDiffuse.addAssign(total.sub(nlS).mul(lightData.lightColor).mul(diffuseContribution).mul(1 / Math.PI));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  finish(builder: any): void {
    super.finish(builder);
    if (!this.mask) return;
    const ctx = builder.context;
    const dbg: N = uSkinDebug;
    const maskCol: N = vec3(this.mask, this.mask.mul(0.18), this.mask.mul(0.12));
    const curvCol: N = vec3(this.lutV, this.lutV.mul(this.lutV), float(1).sub(this.lutV).mul(0.25));
    const nlCol: N = vec3(this.nlDbg.mul(0.5).add(0.5), this.mask, 0);
    const view: N = select(dbg.lessThan(1.5), maskCol, select(dbg.lessThan(2.5), curvCol, select(dbg.lessThan(3.5), this.trans.mul(this.mask.mul(this.mask)), nlCol)));
    ctx.outgoingLight.assign(mix(ctx.outgoingLight, view, step(0.5, dbg)));
  }
}

// ── The material ───────────────────────────────────────────────────────────────────────────────────────────────────

/** A rig's physical node material with the skin lighting model. */
export class SkinNodeMaterial extends THREE.MeshPhysicalNodeMaterial {
  static get type(): string { return 'SkinNodeMaterial'; }
  readonly isSkinNodeMaterial = true;
  /** the body's luminance floor (read per rendered material — `materialReference`) */
  skinFloor0: number = P.yLo0;
  skinFloor1: number = P.yLo1;
  /** the bone mask (bit j = skeleton bone j may be skin) */
  skinBoneMask = 0;
  skinBody = '';
  setupLightingModel(): THREE.PhysicalLightingModel {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const self = this as any;
    return new SkinLightingModel(self.useClearcoat, self.useSheen, self.useIridescence, self.useAnisotropy, self.useTransmission, self.useDispersion);
  }
}

/** Properties never copied from the classic material (three's own `fromMaterial` copies the rest). */
const SKIP = new Set(['uuid', 'id', 'type', '_listeners', 'customProgramCacheKey', 'onBeforeCompile', 'onBeforeRender', 'userData', 'isSkinNodeMaterial', 'skinFloor0', 'skinFloor1', 'skinBoneMask', 'skinBody']);

/** Build a body's skin material from its (possibly DQ-patched) classic material. */
export function buildSkinMaterial(src: THREE.Material, boneNames: readonly string[], bodyKey: string, body: SkinBodyParams | null): SkinNodeMaterial {
  skinLut(); // bake before the first build (and outside any frame the build might be timed in)
  const m = new SkinNodeMaterial();
  const s = src as unknown as Record<string, unknown>;
  const d = m as unknown as Record<string, unknown>;
  for (const key in s) { if (!SKIP.has(key)) d[key] = s[key]; }
  m.userData = { ...src.userData, skinShading: true };
  m.name = `${src.name || 'rig'}#skin`;
  m.skinBody = bodyKey;
  const floor = body?.yFloor ?? [P.yLo0, P.yLo1];
  m.skinFloor0 = floor[0];
  m.skinFloor1 = floor[1];
  m.skinBoneMask = skinBoneMask(boneNames, body?.regions ?? DEFAULT_SKIN_REGIONS);
  m.needsUpdate = true;
  skinStats.materialsBuilt++;
  return m;
}

// ── The registry and the swap ──────────────────────────────────────────────────────────────────────────────────────

interface SkinEntry { orig: THREE.Material; skin: SkinNodeMaterial | null; body: string }
const cache = new WeakMap<THREE.Material, Map<string, SkinNodeMaterial>>();
const live = new Set<WeakRef<THREE.SkinnedMesh>>();
const entries = new WeakMap<THREE.SkinnedMesh, SkinEntry>();
/** Bodies seen (lever): key → { materials, off, floor, regions }. */
const bodies = new Map<string, { materials: Set<SkinNodeMaterial>; off: boolean; params: SkinBodyParams | null; bones: number }>();

/** Whether skin shading is wanted now (the tier flag, the kill switch). */
export function skinShadingWanted(): boolean {
  return activePreset().skinShading === true && !skinKilled();
}

function skinFor(orig: THREE.Material, mesh: THREE.SkinnedMesh, bodyKey: string): SkinNodeMaterial | null {
  const body = skinBodyParams(bodyKey);
  if (body?.off) return null;
  const flags = orig as { isMeshStandardMaterial?: boolean; isMeshStandardNodeMaterial?: boolean; transparent?: boolean };
  if (!(flags.isMeshStandardMaterial || flags.isMeshStandardNodeMaterial) || flags.transparent) return null;
  const bones = mesh.skeleton?.bones?.map((b) => b.name) ?? [];
  const variant = `${orig.userData?.dqSkinning ? 'dq' : 'lbs'}|${bones.length}|${bodyKey}`;
  let byVariant = cache.get(orig);
  if (!byVariant) { byVariant = new Map(); cache.set(orig, byVariant); }
  let m = byVariant.get(variant);
  if (!m) {
    m = buildSkinMaterial(orig, bones, bodyKey, body);
    byVariant.set(variant, m);
    let b = bodies.get(bodyKey);
    if (!b) { b = { materials: new Set(), off: !!body?.off, params: body, bones: bones.length }; bodies.set(bodyKey, b); }
    b.materials.add(m);
  }
  return m;
}

let applied: boolean | null = null;

// ── The per-body geometry bake (curvature + chord) ─────────────────────────────────────────────────────────────────

type GeoState = 'pending' | 'ready' | 'failed';
const geoState = new WeakMap<THREE.BufferGeometry, GeoState>();
const geoPending = new Map<number, THREE.BufferGeometry>();
let geoWorker: Worker | null = null;
let geoWorkerBroken = false;
let geoSeq = 0;
/** A bake landed since the last sync: the next sync re-applies (a mesh waiting on it swaps then). */
let geoDirty = false;
export const skinGeoStats = { requested: 0, baked: 0, failed: 0, syncFallbacks: 0, lastMs: 0, lastWelded: 0, lastHits: 0 };

function copyComponents(a: THREE.BufferAttribute | THREE.InterleavedBufferAttribute, items: number): Float32Array {
  // denormalises and de-interleaves (the served rigs: Int16 positions, Int8 normals — KHR_mesh_quantization)
  const out = new Float32Array(a.count * items);
  for (let i = 0; i < a.count; i++) for (let c = 0; c < items; c++) out[i * items + c] = a.getComponent(i, c);
  return out;
}

function landGeo(g: THREE.BufferGeometry, packed: Float32Array): void {
  g.setAttribute('skinGeo', new THREE.BufferAttribute(packed, 2));
  geoState.set(g, 'ready');
  geoDirty = true;
}

function bakeSync(g: THREE.BufferGeometry, req: SkinGeoRequest): void {
  try {
    skinGeoStats.syncFallbacks++;
    const r = bakeSkinGeometry({ positions: req.positions, normals: req.normals, index: req.index, vertexCount: req.vertexCount });
    skinGeoStats.baked++; skinGeoStats.lastMs = r.ms; skinGeoStats.lastWelded = r.welded; skinGeoStats.lastHits = r.hits;
    landGeo(g, r.packed);
  } catch (e) {
    console.warn('[skin] geometry bake failed', e);
    skinGeoStats.failed++;
    geoState.set(g, 'failed');
  }
}

function geoWorkerOf(): Worker | null {
  if (geoWorkerBroken) return null;
  if (!geoWorker) {
    try {
      geoWorker = new Worker(new URL('../workers/skin_geo_worker.ts', import.meta.url), { type: 'module' });
      geoWorker.onmessage = (ev: MessageEvent<SkinGeoReply>) => {
        const d = ev.data;
        const g = geoPending.get(d.id);
        if (!g) return;
        geoPending.delete(d.id);
        if (d.packed) {
          skinGeoStats.baked++; skinGeoStats.lastMs = d.ms ?? 0; skinGeoStats.lastWelded = d.welded ?? 0; skinGeoStats.lastHits = d.hits ?? 0;
          landGeo(g, d.packed);
        } else { console.warn('[skin] geometry bake failed', d.error); skinGeoStats.failed++; geoState.set(g, 'failed'); }
      };
      geoWorker.onerror = () => {
        // a broken worker: finish what it held on this thread, once each (a hitch, never a missing body)
        geoWorkerBroken = true;
        geoWorker?.terminate(); geoWorker = null;
        const held = [...geoPending.values()]; geoPending.clear();
        for (const g of held) { geoState.delete(g); requestGeo(g); }
      };
    } catch { geoWorkerBroken = true; return null; }
  }
  return geoWorker;
}

function requestGeo(g: THREE.BufferGeometry): void {
  const pos = g.getAttribute('position'), nor = g.getAttribute('normal');
  if (!pos || !nor) { geoState.set(g, 'failed'); skinGeoStats.failed++; return; }
  geoState.set(g, 'pending');
  skinGeoStats.requested++;
  const idx = g.getIndex();
  const req: SkinGeoRequest = {
    id: ++geoSeq,
    positions: copyComponents(pos as THREE.BufferAttribute, 3),
    normals: copyComponents(nor as THREE.BufferAttribute, 3),
    index: idx ? Uint32Array.from(idx.array as ArrayLike<number>) : null,
    vertexCount: pos.count,
  };
  const w = geoWorkerOf();
  if (!w) { bakeSync(g, req); return; }
  geoPending.set(req.id, g);
  const transfers: ArrayBuffer[] = [req.positions.buffer as ArrayBuffer, req.normals.buffer as ArrayBuffer];
  if (req.index) transfers.push(req.index.buffer as ArrayBuffer);
  w.postMessage(req, transfers);
}

/** Whether a geometry carries its bake (asks for it the first time; a failed bake stays plain). */
function skinGeoReady(g: THREE.BufferGeometry): boolean {
  if (g.getAttribute('skinGeo')) return true;
  const st = geoState.get(g);
  if (!st) requestGeo(g);
  return geoState.get(g) === 'ready';
}

/**
 * Register a rig actor's skinned meshes (makeRigActor, after the DQ patch). Every mesh is remembered with its original
 * material; while skin shading is wanted it wears the body's skin material.
 */
export function registerRigSkin(root: THREE.Object3D, bodyKey: string): void {
  const want = skinShadingWanted();
  root.traverse((o) => {
    const sm = o as THREE.SkinnedMesh;
    if (!sm.isSkinnedMesh || Array.isArray(sm.material) || entries.has(sm)) return;
    const orig = sm.material as THREE.Material;
    const e: SkinEntry = { orig, skin: null, body: bodyKey };
    entries.set(sm, e);
    live.add(new WeakRef(sm));
    skinStats.meshesRegistered++;
    if (!bodies.has(bodyKey)) bodies.set(bodyKey, { materials: new Set(), off: !!skinBodyParams(bodyKey)?.off, params: skinBodyParams(bodyKey), bones: sm.skeleton?.bones?.length ?? 0 });
    if (want) applyTo(sm, e, true);
  });
  if (applied === null) applied = want;
}

function applyTo(sm: THREE.SkinnedMesh, e: SkinEntry, on: boolean): void {
  if (on) {
    if (!e.skin) e.skin = skinFor(e.orig, sm, e.body);
    // the skin material reads the baked attribute: wait for it (the plain material keeps drawing meanwhile)
    if (e.skin && sm.material !== e.skin && skinGeoReady(sm.geometry)) { sm.material = e.skin; skinStats.swapsOn++; }
  } else if (sm.material !== e.orig) { sm.material = e.orig; skinStats.swapsOff++; }
}

/** Apply the wanted state to every live actor when it changed (a per-frame boolean check — cheap). */
export function syncSkinShading(force = false): void {
  const want = skinShadingWanted();
  if (!force && want === applied && !geoDirty) return;
  applied = want;
  geoDirty = false;
  skinStats.syncs++;
  for (const ref of live) {
    const sm = ref.deref();
    if (!sm) { live.delete(ref); continue; }
    const e = entries.get(sm);
    if (e) applyTo(sm, e, want);
  }
}

// ── Coverage (texture space, on the GPU) ───────────────────────────────────────────────────────────────────────────

/**
 * The mean skin weight over a skin material's base-colour atlas, measured on the GPU at 128² (the classifier with the
 * body's floor; texture space carries no bones). A diagnostic — the lever calls it on demand.
 */
export async function measureCoverage(renderer: THREE.WebGPURenderer, m: SkinNodeMaterial): Promise<number | null> {
  const map = (m as unknown as { map?: THREE.Texture | null }).map;
  if (!map) return null;
  const mr = (m as unknown as { metalnessMap?: THREE.Texture | null }).metalnessMap ?? null;
  const q = new THREE.MeshBasicNodeMaterial();
  q.toneMapped = false;
  const metal: N = mr ? texture(mr, uv()).b.mul((m as unknown as { metalness: number }).metalness) : float((m as unknown as { metalness: number }).metalness);
  const w: N = skinMaskNode(texture(map, uv()).rgb, metal, float(m.skinFloor0), float(m.skinFloor1));
  q.colorNode = vec4(w, w, w, 1);
  const quad = new THREE.QuadMesh(q);
  const S = 128; // THE READBACK LAW: 128 px × 4 B = a 512 B row (256-aligned)
  const rt = new THREE.RenderTarget(S, S);
  const prev = renderer.getRenderTarget();
  try {
    renderer.setRenderTarget(rt);
    quad.render(renderer);
    const px = await renderer.readRenderTargetPixelsAsync(rt, 0, 0, S, S) as Uint8Array;
    let s = 0; for (let i = 0; i < S * S; i++) s += px[i * 4];
    return +(s / (S * S * 255)).toFixed(4);
  } finally {
    renderer.setRenderTarget(prev);
    rt.dispose(); q.dispose();
  }
}

// ── The lever ──────────────────────────────────────────────────────────────────────────────────────────────────────

/** `window.__skin()` — bodies patched, the classifier, the LUT, the knobs; `.measure(renderer)` for GPU coverage. */
export function skinLeverSnapshot(): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  let meshes = 0, wearing = 0;
  for (const ref of live) { const sm = ref.deref(); if (!sm) continue; meshes++; if ((sm.material as { isSkinNodeMaterial?: boolean }).isSkinNodeMaterial) wearing++; }
  for (const [k, b] of bodies) {
    const mat = [...b.materials][0];
    out[k] = {
      materials: b.materials.size, off: b.off, bones: b.bones,
      floor: mat ? [mat.skinFloor0, mat.skinFloor1] : (b.params?.yFloor ?? [P.yLo0, P.yLo1]),
      regions: b.params?.regions ?? DEFAULT_SKIN_REGIONS, boneMask: mat?.skinBoneMask ?? null, note: b.params?.note ?? '',
    };
  }
  return {
    wanted: skinShadingWanted(), flag: activePreset().skinShading === true, killed: skinKilled(), applied,
    meshes, wearing, bodies: out, stats: { ...skinStats }, geo: { ...skinGeoStats, pending: geoPending.size, workerBroken: geoWorkerBroken },
    knobs: { mix: uSkinMix.value, curvatureScale: uSkinCurvScale.value, transmission: uSkinTransmission.value, debug: uSkinDebug.value },
    classifier: { ...SKIN_CLASSIFIER }, lut: { ...SKIN_LUT, energyMaxBeforeNorm: skinStats.lutEnergyMax },
    regionsOf: (names: string[]) => names.map(regionOfBone),
  };
}

/** All built skin materials (the lever's coverage walk). */
export function skinMaterials(): SkinNodeMaterial[] {
  const out: SkinNodeMaterial[] = [];
  for (const b of bodies.values()) for (const m of b.materials) out.push(m);
  return out;
}

if (typeof window !== 'undefined') {
  const w = window as unknown as Record<string, unknown>;
  const lever = (() => skinLeverSnapshot()) as unknown as Record<string, unknown>;
  lever.setMix = (v: number) => { uSkinMix.value = Math.max(0, Math.min(1, v)); };
  lever.setTransmission = (v: number) => { uSkinTransmission.value = Math.max(0, Math.min(1, v)); };
  lever.setCurvatureScale = (v: number) => { uSkinCurvScale.value = Math.max(0.1, Math.min(8, v)); };
  lever.debug = (v: number) => { uSkinDebug.value = v | 0; };
  lever.sync = () => syncSkinShading(true);
  lever.measure = async (renderer: THREE.WebGPURenderer) => {
    const res: Record<string, number | null> = {};
    for (const m of skinMaterials()) res[`${m.skinBody}#${(m as unknown as { id: number }).id}`] = await measureCoverage(renderer, m);
    return res;
  };
  w.__skin = lever;
}
